/**
 * Locale-aware display formatting. Presentation only — no engineering math.
 *
 * Depth: values arrive from the services in metres. The EN (US) tenant reads
 * imperial (ft), the ES/PT tenants read metric (m). This is the *only* unit
 * conversion the FE does, and it's a pure display transform (the underlying
 * KPI math on the backend is unit-consistent).
 */
import type { Locale, UnitSystem } from "@i18n/locales";
import { LOCALE_META } from "@i18n/locales";

const M_TO_FT = 3.28084;

/** The Intl locale tag we hand to toLocaleString for a given app locale. */
export function intlTag(locale: Locale): string {
  return locale === "en" ? "en-US" : locale === "es" ? "es-AR" : "pt-BR";
}

export function unitSystem(locale: Locale): UnitSystem {
  return LOCALE_META[locale].defaultUnitSystem;
}

/** Format a depth given in metres, in the locale's unit system. */
export function fmtDepth(metres: number | null | undefined, locale: Locale): string {
  if (metres == null || !Number.isFinite(metres)) return "—";
  const tag = intlTag(locale);
  if (unitSystem(locale) === "imperial") {
    return `${Math.round(metres * M_TO_FT).toLocaleString(tag)} ft`;
  }
  return `${Math.round(metres).toLocaleString(tag)} m`;
}

/** Format a plain integer / count with locale grouping. */
export function fmtNum(
  n: number | null | undefined,
  locale: Locale,
  opts?: Intl.NumberFormatOptions,
): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return n.toLocaleString(intlTag(locale), opts);
}

/**
 * Format a money amount. Values are served already in the tenant's reporting
 * currency; we only format. Compact ($8.4M) when `compact` is set.
 */
export function fmtMoney(
  amount: number | null | undefined,
  locale: Locale,
  opts?: { compact?: boolean; currency?: string },
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const currency = opts?.currency ?? "USD";
  return amount.toLocaleString(intlTag(locale), {
    style: "currency",
    currency,
    notation: opts?.compact ? "compact" : "standard",
    maximumFractionDigits: opts?.compact ? 2 : 0,
  });
}

/** Format a signed variance amount with an explicit +/- and colour class. */
export function fmtSignedMoney(
  amount: number | null | undefined,
  locale: Locale,
  currency = "USD",
): string {
  if (amount == null || !Number.isFinite(amount)) return "—";
  const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
  const abs = Math.abs(amount).toLocaleString(intlTag(locale), {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
  return `${sign}${abs}`;
}

/** Percent formatting for a 0–100 value (or null). */
export function fmtPct(pct: number | null | undefined, locale: Locale, digits = 0): string {
  if (pct == null || !Number.isFinite(pct)) return "—";
  return `${pct.toLocaleString(intlTag(locale), { maximumFractionDigits: digits })}%`;
}

/** Hours with one decimal. */
export function fmtHours(h: number | null | undefined, locale: Locale): string {
  if (h == null || !Number.isFinite(h)) return "—";
  return `${h.toLocaleString(intlTag(locale), { maximumFractionDigits: 1 })} h`;
}
